import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "@/lib/utils";
import { combineRefs } from "@/hooks/combine-refs";

export type ScrollAreaElement = React.ElementRef<
  typeof ScrollAreaPrimitive.Root
>;

export type ViewportElement = React.ElementRef<
  typeof ScrollAreaPrimitive.Viewport
>;

export type ViewportProps = React.ComponentProps<
  typeof ScrollAreaPrimitive.Viewport
> & { withIndicator?: boolean };

export type ScrollAreaProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Root
> &
  Omit<
    React.ComponentPropsWithoutRef<
      typeof ScrollAreaPrimitive.ScrollAreaScrollbar
    >,
    "orientation"
  > & {
    orientation?: "horizontal" | "vertical" | "both";
    viewport?: ViewportProps;
  };

const Viewport = React.forwardRef<ViewportElement, ViewportProps>(
  ({ children, className, withIndicator = true, ...props }, ref) => {
    const ref_ = React.useRef<ViewportElement | null>(null);
    const [more, setMore] = React.useState({
      top: false,
      bottom: false,
      left: false,
      right: false,
    });
    React.useEffect(() => {
      const el = ref_.current;
      if (!el) return;

      const handleScroll = () => {
        setMore((s) => ({
          ...s,
          top: el.scrollTop > 0,
          bottom: Math.ceil(el.scrollTop + el.clientHeight) < el.scrollHeight,
        }));
      };

      handleScroll(); // initial check
      el.addEventListener("scroll", handleScroll);
      return () => el.removeEventListener("scroll", handleScroll);
    }, []);

    return (
      <>
        <ScrollAreaPrimitive.Viewport
          {...props}
          ref={combineRefs(ref, ref_)}
          className={cn("h-full w-full rounded-[inherit]", className)}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        {withIndicator && (
          <div
            className={cn(
              "absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-black/5 pointer-events-none opacity-0 transition-opacity",
              more.top && "opacity-100",
            )}
          />
        )}
        {withIndicator && (
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-black/5 pointer-events-none opacity-0 transition-opacity",
              more.bottom && "opacity-100",
            )}
          />
        )}
      </>
    );
  },
);

const ScrollArea = React.forwardRef<ScrollAreaElement, ScrollAreaProps>(
  (
    { className, children, forceMount, orientation, viewport, ...props },
    ref,
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <Viewport {...viewport}>{children}</Viewport>
      {orientation === "both" ? (
        <>
          <ScrollBar forceMount={forceMount} orientation={"horizontal"} />
          <ScrollBar forceMount={forceMount} orientation={"vertical"} />
        </>
      ) : (
        <ScrollBar forceMount={forceMount} orientation={orientation} />
      )}

      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  ),
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors z-10",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar, Viewport };
